import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActionSheetController, IonSearchbar, ModalController } from '@ionic/angular';
import { PessoaService } from 'src/app/core/services/http/pessoa.service';
import { ModalRoleEnum } from '../../enums/modal-role.enum';
import { ModalResult } from '../../interfaces/modal-result.interface';
import { PessoaModel } from '../../models/pessoa.model';
import { HelpersUtil } from '../../utils/helpers.util';
import { CadastroClientesComponent, CadastroClientesDataClose } from '../cadastro-clientes/cadastro-clientes.component';
import { Subscription } from 'rxjs';
import { KeyboardKeysEnum } from '../../enums/keyboard-keys.enum';
import { DialogStateUtil } from '../../utils/dialog-state.util';

@Component({
  selector: 'app-localizar-clientes',
  templateUrl: './localizar-clientes.component.html',
  styleUrls: ['./localizar-clientes.component.scss']
})
export class LocalizarClientesComponent implements OnInit, OnDestroy {
  @ViewChild('searchbar', { static: false }) private searchbar: IonSearchbar;

  private confirmarNovoCadastroIsOpen = false;
  private subs$: Subscription[] = [];

  public campoPesquisaFocado: boolean = false;

  public arrClientesFiltrados: any[] = [];
  public indexClienteSelecionado = -1;

  constructor(
    private actionSheetCtrl: ActionSheetController,
    private modalCtrl: ModalController,
    private pessoaService: PessoaService
  ) { }

  public async cancel() {
    await this.modalCtrl.dismiss(null, ModalRoleEnum.CANCEL);
    
    DialogStateUtil.dialogIsClosed();
  }

  public filtrarClientes(value: string) {
    value = HelpersUtil.prepareValue(value);

    if (this.campoPesquisaFocado) this.indexClienteSelecionado = -1;

    if (!value) {
      this.arrClientesFiltrados = [];
    } else {
      const sub$ = this.pessoaService.getAll(value).subscribe(async res => {
        this.arrClientesFiltrados = res.data;

        if (this.arrClientesFiltrados.length === 0) {
          const confirm = await this.confirmarNovoCadastro();

          if (!confirm) {
            await this.searchbar.setFocus();
            return;
          }

          if (confirm) {
            const contemSomenteNumeros = !isNaN(Number.parseInt(value));

            const cliente = new PessoaModel();

            contemSomenteNumeros ? cliente.celulares[0] = value : cliente.nome = value;

            await this.modalCadastroCliente(cliente);
          }
        }
      });

      this.subs$.push(sub$);
    }
  }

  public async modalCadastroCliente(cliente = new PessoaModel()) {
    const className = 'custom-modal-h' + (cliente.codigo > 0 ? '80' : '40');

    const modal = await this.modalCtrl.create({
      component: CadastroClientesComponent,
      cssClass: className,
      componentProps: {
        cliente
      }
    });

    await modal.present();

    const modalResult: ModalResult<CadastroClientesDataClose> = await modal.onWillDismiss();

    if (modalResult.role === ModalRoleEnum.CONFIRM) {
      const cliente = modalResult.data.cliente;
      this.pessoaService.AddOrUpdate(cliente)
        .subscribe(async res => {
          if (res.success) {
            const dataClose = new LocalizarClientesDataClose(res.data);

            HelpersUtil.executeAfterDelay(async () => await this.modalCtrl.dismiss(dataClose, ModalRoleEnum.CONFIRM));
          }
        });
    }
  }

  public ngOnDestroy(): void {
    this.subs$.forEach(sub$ => sub$.unsubscribe());
  }

  public ngOnInit(): void {
    HelpersUtil.executeAfterDelay(() => this.searchbar.setFocus());
  }

  public async selecionarCliente(cliente: PessoaModel) {
    const dataClose = new LocalizarClientesDataClose(cliente);

    await this.modalCtrl.dismiss(dataClose, ModalRoleEnum.CONFIRM);
  }

  public campoPesquisaFoco(value: boolean) {
    this.campoPesquisaFocado = value;
  }

  @HostListener('document:keydown', ['$event'])
  private async handleKeyboardEvent(event: KeyboardEvent) {
    switch (event.key) {
      case KeyboardKeysEnum.ArrowUp:
        event.preventDefault();
        event.stopPropagation();

        this.handleArrowUpKey();
        break;
      case KeyboardKeysEnum.ArrowDown:
        event.preventDefault();
        event.stopPropagation();

        this.handleArrowDownKey();

        break;

      case KeyboardKeysEnum.F8:
        event.preventDefault();
        event.stopPropagation();

        this.handleF8Key();

        break;
      case KeyboardKeysEnum.Enter:
        const cliente = this.arrClientesFiltrados[this.indexClienteSelecionado];

        if (cliente) {
          await this.selecionarCliente(cliente);
        }

        break;
      case KeyboardKeysEnum.Escape:
        if (this.confirmarNovoCadastroIsOpen) {
          (await this.actionSheetCtrl.getTop()).dismiss();
          return;
        }
        
        await this.cancel();
        break;
    }
  }

  private async confirmarNovoCadastro(): Promise<boolean> {
    const actionSheet = await this.actionSheetCtrl.create({
      header: 'Cliente não encontrado. Deseja cadastrar?',
      buttons: [
        {
          text: 'Sim',
          role: ModalRoleEnum.CONFIRM,
          id: 'btnSimActionSheet'
        },
        {
          text: 'Não',
          role: ModalRoleEnum.CANCEL,
          id: 'btnNaoActionSheet'
        },
      ],
      backdropDismiss: false
    });

    await actionSheet.present();

    this.confirmarNovoCadastroIsOpen = true;

    const btnSim = document.querySelector('#btnSimActionSheet') as HTMLButtonElement;

    HelpersUtil.executeAfterDelay(() => btnSim.focus(), 1);

    const { role } = await actionSheet.onWillDismiss();

    this.confirmarNovoCadastroIsOpen = false;

    return role === ModalRoleEnum.CONFIRM;
  }

  private handleArrowDownKey() {
    if (this.confirmarNovoCadastroIsOpen) {
      const btnNao = document.querySelector('#btnNaoActionSheet') as HTMLButtonElement;

      btnNao.focus();
      return;
    }

    this.indexClienteSelecionado = HelpersUtil.selectNextItem(this.indexClienteSelecionado, this.arrClientesFiltrados.length);

    const element = document.querySelector(`[data-cli-index="${this.indexClienteSelecionado}"]`);

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private handleArrowUpKey() {
    if (this.confirmarNovoCadastroIsOpen) {
      const btnSim = document.querySelector('#btnSimActionSheet') as HTMLButtonElement;

      btnSim.focus();
      return;
    }

    this.indexClienteSelecionado = HelpersUtil.selectPreviousItem(this.indexClienteSelecionado);

    const element = document.querySelector(`[data-cli-index="${this.indexClienteSelecionado}"]`);

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private handleF8Key() {
    this.searchbar.setFocus()
  }
}

export class LocalizarClientesDataClose {
  constructor(public cliente: PessoaModel) { }
}
